import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import Sidebar from '../Sidebar';
import { MenuIcon } from '../../assets/icons';
import DashboardUserControls from '../DashboardHeader/DashboardUserControls';
import FloatBackButton from '../FloatBackButton';

export default function Layout({
  children,
  hideHeader = true,
  mainPaddingClassName = 'px-6 py-12',
  showUserControls = true,
  topBarExtra = null,
  floatBack = null,
}) {
  const location = useLocation();
  const isSendMoneyPage = location.pathname.startsWith('/send-money');
  const shouldShowUserControls = showUserControls && !isSendMoneyPage;
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const sidebarWidth = 232;
  const mobileChromeButtonClass =
    'flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[var(--color-border)] bg-[var(--white)] text-[var(--text-secondary)] shadow-[var(--shadow-sm)] transition-colors hover:border-[var(--n200)] hover:text-[var(--n800)]';

  return (
    <div className='flex min-h-screen bg-[var(--bg)]'>
      <div
        className='hidden md:block flex-shrink-0 transition-all duration-300 ease-in-out overflow-visible'
        style={{
          width: sidebarOpen ? sidebarWidth : 0,
        }}
      >
        <Sidebar isOpen={sidebarOpen} onToggle={toggleSidebar} isMobile={false} />
      </div>

      <div
        className={`flex min-w-0 flex-1 flex-col transition-all duration-300 ease-in-out md:mx-auto md:max-w-[1180px] ${mainPaddingClassName}`}
        style={{
          width: isMobile ? '100vw' : `calc(100vw - ${sidebarOpen ? sidebarWidth : 0}px)`,
        }}
      >
        {!hideHeader && (
          <header className='flex items-center justify-between border-b border-border bg-card px-4 py-3'>
            <div className='flex items-center space-x-3'>
              <button
                type='button'
                onClick={toggleSidebar}
                className='rounded-md p-2 transition-colors hover:bg-accent hover:text-accent-foreground'
                aria-label='Toggle sidebar'
              >
                <MenuIcon className='h-3 w-3' />
              </button>
              <div className='flex items-center space-x-2'>
                <h1 className='text-xl font-semibold text-card-foreground'>Fx Dashboard</h1>
              </div>
            </div>
          </header>
        )}

        <main className='relative w-full'>
          {hideHeader && isMobile ? (
            <div className='mb-5 flex items-center justify-between gap-3 md:hidden'>
              <div className='flex items-center gap-2.5'>
                <button type='button' onClick={toggleSidebar} className={mobileChromeButtonClass} aria-label='Open sidebar'>
                  <MenuIcon className='h-4 w-4' />
                </button>
                {floatBack?.onClick ? (
                  <FloatBackButton
                    variant='inline'
                    onClick={floatBack.onClick}
                    label={floatBack.label}
                    ariaLabel={floatBack.ariaLabel}
                  />
                ) : null}
              </div>
              {shouldShowUserControls || topBarExtra ? (
                <div className='flex shrink-0 items-center justify-end gap-2'>
                  {topBarExtra}
                  {shouldShowUserControls ? <DashboardUserControls /> : null}
                </div>
              ) : null}
            </div>
          ) : null}

          {!isMobile && floatBack?.onClick ? (
            <FloatBackButton onClick={floatBack.onClick} label={floatBack.label} ariaLabel={floatBack.ariaLabel} />
          ) : null}
          {!isMobile && (shouldShowUserControls || topBarExtra) ? (
            <div className='pointer-events-none absolute right-0 top-0 z-20 flex items-center justify-end gap-2'>
              {topBarExtra ? <div className='pointer-events-auto'>{topBarExtra}</div> : null}
              {shouldShowUserControls ? (
                <div className='pointer-events-auto'>
                  <DashboardUserControls />
                </div>
              ) : null}
            </div>
          ) : null}
          {children}
        </main>
      </div>

      <div className='md:hidden'>
        <Sidebar isOpen={sidebarOpen} onToggle={toggleSidebar} isMobile />
      </div>
    </div>
  );
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  hideHeader: PropTypes.bool,
  mainPaddingClassName: PropTypes.string,
  showUserControls: PropTypes.bool,
  topBarExtra: PropTypes.node,
  floatBack: PropTypes.shape({
    onClick: PropTypes.func.isRequired,
    label: PropTypes.string,
    ariaLabel: PropTypes.string,
  }),
};
